import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import LifeChoiceProvider from "context/lifeChoice";
import Block from "pages/Block";
import Course from "pages/Course";
import ResponseForm from "pages/ResponseForm";
import Maintinance from "pages/Maintinance";
import PersonalAI from "pages/PersonalAI";
import Support from "pages/Support";
import HollandCode from "pages/PersonalAI/HollandCode";
import { useUserAuth } from "context/authProvider";
import NotFoundPage from "pages/NotFoundPage";
import HomeImages from "context/HomeMajorImages";
import Home from "pages/Home";
import { useSocket } from "context/socket";
import useUser from "context/hooks/user";
import { useModal } from "context/hooks/useModal";
import { useEffect, useRef, useState } from "react";
import { t } from "i18next";
import AskForActivate from "pages/AskForActivate";
import { clearAllLocalStorage } from "utils/utility";
import ChatPopupIcon from "assets/icons/ChatPopupIcon";
import Chatbot from "pages/Chatbot";
import { useChatBotSettings } from "pages/Chatbot/services/chatService";

const HomeWrapper = () => (
  <HomeImages>
    <Home />
  </HomeImages>
);

export default function HomeRoutes() {
  const { user, setUser } = useUserAuth();
  const { data: userDetail } = useUser();
  const { showModal } = useModal();
  const [showChatBot, setShowChatBot] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);
  const { data: chatbotSettings, isLoading: loadingChatbotSettings } =
    useChatBotSettings();
  const location = useLocation();

  const { socket } = useSocket();

  useEffect(() => {
    if (socket && userDetail?.session_id) {
      socket.emit("join-room", { room: userDetail?.session_id });
      socket.on("switch-session", ({ session_id }) => {
        if (session_id === userDetail?.session_id) return;
        showModal(<SessionTaken />, "sm");
        clearAllLocalStorage();
        setUser(null);
      });
    }
  }, [socket, userDetail?.session_id]);

  const SessionTaken = () => {
    return (
      <div>
        <h2 className="text-center py-10 text-xl">
          {t("popUpTitleAccountAlreadySigned")}
        </h2>
      </div>
    );
  };

  useEffect(() => {
    if (showChatBot) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "auto";
    }
    () => {
      document.documentElement.style.overflow = "auto";
    };
  }, [showChatBot]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(true);

      // Clear the previous timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Set a timeout to turn off isScrolling after 150ms of no scroll
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Hide chatbot on ResponseForm page
  const shouldShowChatbot = chatbotSettings && 
    chatbotSettings.active !== false && 
    !location.pathname.startsWith("/response-form/");

  return (
    <>
      {shouldShowChatbot ? (
        <div
          className={`fixed z-10 bottom-4 right-4 size-20 rounded-full bg-transparent ${
            isScrolling ? "hidden" : "block"
          }`}
        >
          <ChatPopupIcon
            className="cursor-pointer hover:scale-110 transition-all ease-in-out duration-300"
            onClick={() => setShowChatBot(!showChatBot)}
          />
        </div>
      ) : (
        ""
      )}
      {showChatBot ? (
        <div className="fixed z-20 top-0 left-0 h-dvh w-screen">
          <Chatbot onClose={() => setShowChatBot(false)} />
        </div>
      ) : (
        ""
      )}
      <Routes>
        <Route
          path="/"
          element={user ? <HomeWrapper /> : <Navigate to="/user-auth/login" />}
        />
        <Route path="/personal-ai">
          <Route
            index
            element={user ? <PersonalAI /> : <Navigate to="/user-auth/login" />}
          />
          <Route
            path="quiz/:id"
            element={
              user ? <HollandCode /> : <Navigate to="/user-auth/login" />
            }
          />
        </Route>
        <Route
          path="/support"
          element={user ? <Support /> : <Navigate to="/user-auth/login" />}
        />

        <Route
          path="/:id"
          element={
            user ? (
              <LifeChoiceProvider>
                <Block />
              </LifeChoiceProvider>
            ) : (
              <Navigate to="/user-auth/login" />
            )
          }
        />
        <Route
          path="/year:id/course:id"
          element={user ? <Course /> : <Navigate to="/user-auth/login" />}
        ></Route>
        <Route
          path="/response-form/:id"
          element={user ? <ResponseForm /> : <Navigate to="/user-auth/login" />}
        />
        <Route
          path="/activate-key"
          element={
            user ? <AskForActivate /> : <Navigate to="/user-auth/login" />
          }
        />
        <Route path="/maintinance" element={<Maintinance />} />
        <Route
          path="*"
          element={user ? <NotFoundPage /> : <Navigate to="/user-auth/login" />}
        />
      </Routes>
    </>
  );
}
